import { useState } from "react";
import { authApi } from "../../services/apiClient";
import { APP_FONT_FAMILY } from "../../shared/theme";

export default function LoginPage({ onLoginSuccess, error, successInfo }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // credentials | twoFactor
  const [challengeToken, setChallengeToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [localError, setLocalError] = useState("");

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError("");
    try {
      const payload = await authApi.login(email.trim(), password);
      if (payload.two_factor_required && payload.challenge_token) {
        setChallengeToken(payload.challenge_token);
        setStep("twoFactor");
        setTwoFactorCode("");
        setRememberDevice(false);
      } else if (payload.token) {
        onLoginSuccess?.(payload);
      } else {
        setLocalError("Réponse de connexion inattendue.");
      }
    } catch (err) {
      setLocalError(err.message || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFactor = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError("");
    try {
      const payload = await authApi.completeTwoFactorLogin(challengeToken, twoFactorCode.trim(), rememberDevice);
      if (payload?.token) {
        onLoginSuccess?.(payload);
      } else {
        setLocalError("Réponse inattendue.");
      }
    } catch (err) {
      setLocalError(err.message || "Code refusé.");
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setChallengeToken("");
    setTwoFactorCode("");
    setLocalError("");
  };

  const combinedError = localError || error || "";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f9fafb",
        fontFamily: APP_FONT_FAMILY,
        padding: 20,
      }}
    >
      {step === "credentials" ? (
        <form
          onSubmit={submitCredentials}
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            borderRadius: 12,
            padding: 28,
            border: "1px solid #e4e7ec",
            boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
          }}
        >
          <h1 style={{ margin: 0, fontSize: 24, color: "#111827" }}>Connexion RGPD Suite</h1>
          <p style={{ marginTop: 8, marginBottom: 20, color: "#6b7280", fontSize: 13 }}>
            Accédez à votre espace de conformité RGPD.
          </p>

          <label style={{ display: "block", fontSize: 12, color: "#374151", marginBottom: 6 }}>E-mail</label>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: 12 }}
          />

          <label style={{ display: "block", fontSize: 12, color: "#374151", marginBottom: 6 }}>Mot de passe</label>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: 14 }}
          />

          {successInfo && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: "#ecfdf3",
                color: "#065f46",
                border: "1px solid #86efac",
                borderRadius: 8,
                fontSize: 12,
              }}
            >
              {successInfo}
            </div>
          )}

          {combinedError && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                borderRadius: 8,
                fontSize: 12,
              }}
            >
              {combinedError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "11px 12px",
              borderRadius: 8,
              border: "none",
              background: "#2563eb",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>

          <div style={{ marginTop: 14, textAlign: "center" }}>
            <a
              href={`${window.location.pathname || "/"}?page=forgot-password`}
              style={{ fontSize: 13, color: "#2563eb", fontWeight: 600, textDecoration: "none" }}
            >
              Mot de passe oublié ?
            </a>
          </div>
        </form>
      ) : (
        <form
          onSubmit={submitTwoFactor}
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#fff",
            borderRadius: 12,
            padding: 28,
            border: "1px solid #e4e7ec",
            boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
          }}
        >
          <h1 style={{ margin: 0, fontSize: 22, color: "#111827" }}>Vérification en deux étapes</h1>
          <p style={{ marginTop: 8, marginBottom: 18, color: "#6b7280", fontSize: 13, lineHeight: 1.5 }}>
            Saisissez le code à 6 chiffres affiché dans votre application d’authentification (Google Authenticator ou équivalent).
          </p>

          <label style={{ display: "block", fontSize: 12, color: "#374151", marginBottom: 6 }}>Code</label>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="123456"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: 14, letterSpacing: "0.1em", fontSize: 18 }}
          />

          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer", fontSize: 13, color: "#374151" }}>
            <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />
            Faire confiance à cet appareil (ne plus demander le code pendant 30 jours sur cet appareil)
          </label>

          {combinedError && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                borderRadius: 8,
                fontSize: 12,
              }}
            >
              {combinedError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || twoFactorCode.trim().length < 6}
            style={{
              width: "100%",
              padding: "11px 12px",
              borderRadius: 8,
              border: "none",
              background: "#2563eb",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              opacity: loading || twoFactorCode.trim().length < 6 ? 0.65 : 1,
              marginBottom: 10,
            }}
          >
            {loading ? "Vérification…" : "Valider"}
          </button>
          <button type="button" onClick={backToCredentials} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            ← Retour
          </button>
        </form>
      )}
    </div>
  );
}
